import { enableMapSet } from 'immer';
import { immer } from 'zustand/middleware/immer';
import { create } from 'zustand/react';
import { AgentInboxModel } from '../table/types/types';
import { getAxios } from '../../../lib/axios';
import { AgentInboxEndpoints } from '../../../constants/endpoints/agentInboxEndpoints.ts';
import { applyOrder, clearInboxArrays } from '../utils/inboxStoreUtils';
import { useInboxHubStore } from '../hubs/InboxHub';
import ActivityAlertLevel from '../../../enums/activities/ActivityAlertLevel';
import ActivityStatus from '../../../enums/activities/ActivityStatus';

enableMapSet();

export type AgentInboxItem = {
  value: AgentInboxModel;
  order?: number;
  changedValues?: Set<string>;
  selected?: boolean;
};

type AgentInboxCounts = {
  Total: number;
  NewReply: number;
  Urgent: number;
  Today: number;
  Tomorrow: number;
  Upcoming: number;
  Waiting: number;
  Holding: number;
  WaitingForPayment: number;
};

export type AgentInboxItemStore = {
  activityMap: Map<number, AgentInboxItem>;
  active: number[];
  inactive: number[];
  replies: number[];
  followUpReplies: number[];
  followUpActive: number[];
  followUpInactive: number[];
  priorityList: number[];
  selectedIds: number[];
  lastSelectedId: number | null;
  counts: AgentInboxCounts;
  newReplyActivityIds: Set<number>;
  sortByReceived: boolean;
  init: () => Promise<void>;
  handleUpdate: (item: AgentInboxModel) => void;
  handleBulkUpdate: (item: AgentInboxModel[]) => void;
  handleBulkRemove: (item: number[]) => void;
  handleRemove: (id: number) => void;
  processUpdates: (items: AgentInboxModel[]) => void;
  setSelectedIds: (id: number[]) => void;
  toggleSelection: (id: number) => void;
  selectSingle: (id: number) => void;
  selectRange: (startId: number, endId: number) => void;
  selectAll: () => void;
  clearSelection: () => void;
  swapPriority: (idA: number, idxA: number, idB: number, idxB: number) => void;
  removeFromPriority: (id: number) => void;
  updateCounts: () => void;
  addNewReply: (activityId: number) => void;
  removeNewReply: (activityId: number) => void;
  setSortByReceived: (value: boolean) => void;
};

//const MaxChanges = 3;

const calculateInboxCounts = (
  activityMap: Map<number, AgentInboxItem>,
  newReplyActivityIds: Set<number>
): AgentInboxCounts => {
  const activities = Array.from(activityMap.values()).map((item) => item.value);

  return {
    Total: activities.length,
    NewReply: newReplyActivityIds.size,
    Urgent: activities.filter((a) => a.ActivityAlertLevel === ActivityAlertLevel.Urgent).length,
    Today: activities.filter((a) => a.ActivityAlertLevel === ActivityAlertLevel.Today).length,
    Tomorrow: activities.filter((a) => a.ActivityAlertLevel === ActivityAlertLevel.Tomorrow).length,
    Upcoming: activities.filter((a) => a.ActivityAlertLevel === ActivityAlertLevel.Upcoming).length,
    Waiting: activities.filter((a) => a.ActivityStatus === ActivityStatus.Waiting).length,
    Holding: activities.filter((a) => a.ActivityStatus === ActivityStatus.Holding).length,
    WaitingForPayment: activities.filter((a) => a.ActivityStatus === ActivityStatus.WaitingForPayment).length
  };
};

// Helper function to create updated item with change tracking
// const createUpdatedItem = (update: AgentInboxModel, oldItem: AgentInboxItem | undefined): AgentInboxItem => {
//   const changedValues = oldItem ? new Set(oldItem.changedValues) : new Set<string>();
//   const key = oldItem ? getChangedKey(update, oldItem.value) : undefined;
//   if (key) {
//     changedValues.add(key as string);
//     if (changedValues.size > MaxChanges) {
//       const first = changedValues.values().next().value;
//       if (first) {
//         changedValues.delete(first);
//       }
//     }
//   }
//
//   return {
//     value: update,
//     order: oldItem?.order,
//     changedValues,
//     selected: oldItem?.selected
//   };
// };

export const useAgentInboxItemStore = create<AgentInboxItemStore>()(
  immer((set, get) => ({
    inactive: [],
    counts: {
      Total: 0,
      NewReply: 0,
      Urgent: 0,
      Today: 0,
      Tomorrow: 0,
      Upcoming: 0,
      Waiting: 0,
      Holding: 0,
      WaitingForPayment: 0
    },
    newReplyActivityIds: new Set(),
    selectedIds: [],
    lastSelectedId: null,
    sortByReceived: false,
    priorityList: [],
    activityMap: new Map(),
    active: [],
    replies: [],
    followUpReplies: [],
    followUpActive: [],
    followUpInactive: [],
    init: async () => {
      useInboxHubStore.getState().setIsUpdating(true);
      const { data: items } = await getAxios().get<AgentInboxModel[]>(AgentInboxEndpoints.AgentInbox.overview);
      const { data: priorityList } = await getAxios().get<AgentInboxModel[]>(
        AgentInboxEndpoints.AgentInbox.priorityList
      );
      set((state) => {
        state.activityMap = new Map();
        clearInboxArrays(state);
        items.forEach((value) => {
          state.activityMap.set(value.Id, { value });
        });
        applyOrder(state, true);

        state.priorityList = [];
        state.priorityList = priorityList.flatMap((item) => item.Id);
        state.counts = calculateInboxCounts(state.activityMap, state.newReplyActivityIds);
      });
      useInboxHubStore.getState().setIsUpdating(false);
    },
    removeFromPriority: (id) => {
      set((state) => {
        state.priorityList = state.priorityList.filter((r) => r != id);
      });
      useInboxHubStore.getState().setIsPriorityUpdating(false);
    },
    swapPriority: (idA, idxA, idB, idxB) => {
      set((state) => {
        const itemA = state.activityMap.get(idA);
        const itemB = state.activityMap.get(idB);
        if (itemA && itemB) {
          state.activityMap.set(idA, {
            ...itemA,
            value: {
              ...itemA?.value,
              ManagerIndex: idxA
            }
          });
          state.activityMap.set(idB, {
            ...itemB,
            value: {
              ...itemB?.value,
              ManagerIndex: idxB
            }
          });
        }
        state.priorityList.sort((a, b) => {
          const itemA = state.activityMap.get(a);
          const itemB = state.activityMap.get(b);
          const idxA = itemA?.value.ManagerIndex;
          const idxB = itemB?.value.ManagerIndex;

          // Both undefined: preserve original order (treated equal)
          if (idxA === undefined && idxB === undefined) {
            return 0;
          }
          // A undefined => push A to bottom
          if (idxA === undefined) {
            return 1;
          }
          // B undefined => push B to bottom
          if (idxB === undefined) {
            return -1;
          }
          // Both defined: normal numeric ascending
          return idxA - idxB;
        });
      });
      useInboxHubStore.getState().setIsPriorityUpdating(false);
    },
    setSelectedIds: (items) =>
      set((state) => {
        state.selectedIds = items;
      }),
    toggleSelection: (id) =>
      set((state) => {
        const index = state.selectedIds.indexOf(id);
        const item = state.activityMap.get(id);
        if (index > -1) {
          state.selectedIds.splice(index, 1);
          if (item) {
            state.activityMap.set(id, { ...item, selected: false });
          }
        } else {
          state.selectedIds.push(id);
          if (item) {
            state.activityMap.set(id, { ...item, selected: true });
          }
        }
        state.lastSelectedId = id;
      }),
    selectSingle: (id) =>
      set((state) => {
        // Clear all current selections
        for (const [itemId, item] of state.activityMap) {
          if (item.selected) {
            state.activityMap.set(itemId, { ...item, selected: false });
          }
        }

        // Select only the clicked item
        const item = state.activityMap.get(id);
        if (item) {
          state.activityMap.set(id, { ...item, selected: true });
          state.selectedIds = [id];
          state.lastSelectedId = id;
        }
      }),
    selectRange: (startId, endId) =>
      set((state) => {
        // Get all visible items in their display order
        const allVisibleItems = [
          ...state.replies,
          // ...state.followUpReplies,
          ...state.active,
          // ...state.followUpActive,
          ...state.inactive
          // ...state.followUpInactive
        ];

        const startIndex = allVisibleItems.indexOf(startId);
        const endIndex = allVisibleItems.indexOf(endId);

        if (startIndex === -1 || endIndex === -1) return;

        // Determine the range (start to end, inclusive)
        const minIndex = Math.min(startIndex, endIndex);
        const maxIndex = Math.max(startIndex, endIndex);

        // Get all IDs in the range
        const rangeIds = allVisibleItems.slice(minIndex, maxIndex + 1);

        // Toggle selection for items in range
        const newSelectedIds = new Set(state.selectedIds);
        rangeIds.forEach((id) => {
          if (newSelectedIds.has(id)) {
            newSelectedIds.delete(id); // Deselect if already selected
          } else {
            newSelectedIds.add(id); // Select if not selected
          }
        });

        state.selectedIds = Array.from(newSelectedIds);

        // Update selection state in the activity map
        rangeIds.forEach((id) => {
          const item = state.activityMap.get(id);
          if (item) {
            const isSelected = newSelectedIds.has(id);
            state.activityMap.set(id, { ...item, selected: isSelected });
          }
        });

        state.lastSelectedId = endId;
      }),
    selectAll: () =>
      set((state) => {
        state.selectedIds = Array.from(state.activityMap.keys());
        for (const [id, item] of state.activityMap) {
          state.activityMap.set(id, { ...item, selected: true });
        }
      }),
    clearSelection: () =>
      set((state) => {
        for (const [id, item] of state.activityMap) {
          if (item.selected) {
            state.activityMap.set(id, { ...item, selected: false });
          }
        }
        state.selectedIds = [];
      }),
    handleBulkUpdate: (items) => {
      set((state) => {
        let calcPriorityList = false;
        items.forEach((item) => {
          const old = state.activityMap.get(item.Id);
          if (old) {
            if (item.ManagerIndex != old.value.ManagerIndex) {
              calcPriorityList = true;
            }
          }
          state.activityMap.set(item.Id, { value: item });
        });
        clearInboxArrays(state);
        applyOrder(state, true);
        state.counts = calculateInboxCounts(state.activityMap, state.newReplyActivityIds);
        if (calcPriorityList) {
          state.priorityList = Array.from(state.activityMap.values())
            .filter((r) => typeof r.value.ManagerIndex == 'number')
            .sort((a, b) => (a.value.ManagerIndex ?? 0) - (b.value.ManagerIndex ?? 0))
            .flatMap((r) => r.value.Id);
        }
      });
      useInboxHubStore.getState().setIsUpdating(false);
    },
    handleBulkRemove: (items) => {
      set((state) => {
        let calcPriorityList = false;
        items.forEach((item) => {
          const old = state.activityMap.get(item);
          if (old) {
            if (typeof old.value.ManagerIndex == 'number') {
              calcPriorityList = true;
            }
          }
          state.activityMap.delete(item);
          state.newReplyActivityIds.delete(item);
        });
        clearInboxArrays(state);
        applyOrder(state, true);
        state.counts = calculateInboxCounts(state.activityMap, state.newReplyActivityIds);
        if (calcPriorityList) {
          state.priorityList = Array.from(state.activityMap.values())
            .filter((r) => typeof r.value.ManagerIndex == 'number')
            .sort((a, b) => (a.value.ManagerIndex ?? 0) - (b.value.ManagerIndex ?? 0))
            .flatMap((r) => r.value.Id);
        }
      });
      useInboxHubStore.getState().setIsUpdating(false);
    },
    handleUpdate: () => {
      // set((state) => {
      //   const oldItem = state.activityMap.get(update.Id);
      //   state.activityMap.set(update.Id, createUpdatedItem(update, oldItem));
      //   applyOrder(state);
      //   const newItem = state.activityMap.get(update.Id)!;
      //   applySwap(state, oldItem, newItem);
      //   state.counts = calculateInboxCounts(state.activityMap, state.newReplyActivityIds);
      // });
      // useInboxHubStore.getState().setIsUpdating(false);
    },

    handleRemove: (id) => {
      //TODO remove/merge with bulk remove
      set((state) => {
        const item = state.activityMap.get(id);
        if (!item) {
          return;
        }
        if (item.value.ManagerIndex != undefined) {
          state.priorityList = state.priorityList.filter((r) => r !== id);
        }
        clearInboxArrays(state);
        state.activityMap.delete(id);
        state.newReplyActivityIds.delete(id);
        applyOrder(state, true);
        state.counts = calculateInboxCounts(state.activityMap, state.newReplyActivityIds);
      });
      useInboxHubStore.getState().setIsUpdating(false);
    },

    updateCounts: () =>
      set((state) => {
        state.counts = calculateInboxCounts(state.activityMap, state.newReplyActivityIds);
      }),

    addNewReply: (activityId) =>
      set((state) => {
        state.newReplyActivityIds.add(activityId);
        state.counts = calculateInboxCounts(state.activityMap, state.newReplyActivityIds);
      }),

    removeNewReply: (activityId) =>
      set((state) => {
        state.newReplyActivityIds.delete(activityId);
        state.counts = calculateInboxCounts(state.activityMap, state.newReplyActivityIds);
      }),

    setSortByReceived: (value) =>
      set((state) => {
        state.sortByReceived = value;
        clearInboxArrays(state);
        applyOrder(state, true);
      }),

    processUpdates: (items) => {
      const state = get();
      //TODO further optimizations
      // Base threshold: 3, scale with inbox size (10%), max 20 items for single updates
      // const inboxSize = state.activityMap.size;
      // const threshold = Math.min(20, Math.max(3, Math.floor(inboxSize * 0.1)));
      //
      // if (items.length <= threshold) {
      //   // Use individual updates for small batches to preserve selective rendering
      //   items.forEach((item) => {
      //     state.handleUpdate(item);
      //   });
      // } else {
      // Use bulk update for larger batches to minimize re-renders
      state.handleBulkUpdate(items);
      //     }
    }
  }))
);
